import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import usePageTitle from '../hooks/usePageTitle';
import ShohAI from '../components/ShohAI';
import CustomerVisitCalendar from '../components/CustomerVisitCalendar';
import ActiveShiftView from '../components/ActiveShiftView';
import axios from 'axios';
import { getFileUrl } from '../config/api';
import { toLocalDateString, parseLocalDate, getTodayLocal } from '../utils/dateUtils';
import PendingReviews from '../components/PendingReviews';
import CountdownTimer from '../components/CountdownTimer';

interface Job {
  _id: string;
  title: string;
  description: string;
  serviceType: string;
  location: {
    address: string;
    city: string;
  };
  schedule: {
    startDate: string;
    endDate: string;
    preferredTime: string;
  };
  visitSchedule?: Array<{
    _id: string;
    date: string;
    isVisitDay: boolean;
    notes?: string;
    time?: string;
    status?: string;
    actualEndTime?: string;
  }>;
  contract: {
    type: string;
    rate: number;
  };
  assignedCaregiver: {
    _id: string;
    firstName: string;
    lastName: string;
    phone?: string;
    email?: string;
    profilePhoto?: string;
  };
  status: string;
}

interface NextVisit {
  date: Date;
  job: Job;
  notes?: string;
  time?: string;
  visitId?: string;
  status?: string;
  visit?: any;
}

const CustomerDashboard: React.FC = () => {
  usePageTitle('Care Seeker Dashboard');
  const navigate = useNavigate();
  const { user } = useAuth();
  const [myJobs, setMyJobs] = useState<Job[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [editingTomorrowNote, setEditingTomorrowNote] = useState(false);
  const [tomorrowNoteInput, setTomorrowNoteInput] = useState('');
  const [editingTime, setEditingTime] = useState(false);
  const [startTime, setStartTime] = useState('');
  const [endTime, setEndTime] = useState('');
  const [timeError, setTimeError] = useState('');  const [successMessage, setSuccessMessage] = useState('');
  const [updating, setUpdating] = useState(false);
  
  const fetchMyJobs = useCallback(async () => {
    try {
      if (!user?.id) return;
      const response = await axios.get(`/api/jobs/customer/${user.id}`);
      setMyJobs(response.data.data || []);
      setLoading(false);
    } catch (error) {
      console.error('Error fetching my jobs:', error);
      setLoading(false);
    }
  }, [user?.id]);

  useEffect(() => {
    fetchMyJobs();
  }, [fetchMyJobs]);

  const getNextVisit = (): NextVisit | null => {
    const activeJobs = myJobs.filter(job => job.status === 'in-progress' || job.status === 'accepted');
    
    if (activeJobs.length === 0) {
      return null;
    }

    const today = getTodayLocal();
    let nextVisit: NextVisit | null = null;
    let earliestDate: Date | null = null;

    for (const job of activeJobs) {
      const startDate = parseLocalDate(job.schedule.startDate);
      const endDate = parseLocalDate(job.schedule.endDate);
      
      const checkStartDate = today > startDate ? today : startDate;
      for (let d = new Date(checkStartDate); d <= endDate; d.setDate(d.getDate() + 1)) {
          
          const dateStr = toLocalDateString(d);
          const visitScheduleEntry = job.visitSchedule?.find((vs: any) => {
            return toLocalDateString(vs.date) === dateStr;
          });
          
          if (visitScheduleEntry && visitScheduleEntry.isVisitDay === false) {
            continue;
          }

          // Skip if this visit is already completed (has actual end time)
          if (visitScheduleEntry && (visitScheduleEntry.actualEndTime || visitScheduleEntry.status === 'completed')) {
             continue;
          }
          
          
          if (!earliestDate || d < earliestDate) {
            earliestDate = new Date(d);
            nextVisit = {
              date: new Date(d),
              job: job,
              notes: visitScheduleEntry?.notes,
              time: visitScheduleEntry?.time || job.schedule.preferredTime, // Per-day time override if exists or preferred
              visitId: visitScheduleEntry?._id,
              status: visitScheduleEntry?.status,
              visit: visitScheduleEntry
            };
          }
          break;
      }
    }

    return nextVisit;
  };

  const validateTimeDuration = (start: string, end: string): boolean => {
    if (!start || !end) {
      setTimeError('Please select both start and end times');
      return false;
    }

    const [startHour, startMin] = start.split(':').map(Number);
    const [endHour, endMin] = end.split(':').map(Number);
    
    const startMinutes = startHour * 60 + startMin;
    const endMinutes = endHour * 60 + endMin;
    
    if (endMinutes <= startMinutes) {
      setTimeError('End time must be after start time');
      return false;
    }
    
    const durationMinutes = endMinutes - startMinutes;
    const durationHours = durationMinutes / 60;
    
    if (durationHours > 8) {
      setTimeError('Visit duration cannot exceed 8 hours');
      return false;
    }
    
    setTimeError('');
    return true;
  };

  const formatTimeRange = (start: string, end: string): string => {
    const formatTime = (time: string) => {
      const [hour, min] = time.split(':').map(Number);
      const period = hour >= 12 ? 'PM' : 'AM';
      const displayHour = hour > 12 ? hour - 12 : hour === 0 ? 12 : hour;
      return `${displayHour}:${min.toString().padStart(2, '0')} ${period}`;
    };
    return `${formatTime(start)} - ${formatTime(end)}`;
  };

  const parseTimeRange = (timeRange: string): { start: string; end: string } | null => {
    // Parse formats like "9:00 AM - 5:00 PM" or "09:00 - 17:00"
    const match = timeRange.match(/(\d{1,2}):(\d{2})\s*(AM|PM)?\s*-\s*(\d{1,2}):(\d{2})\s*(AM|PM)?/i);
    if (!match) return null;
    
    let startHour = parseInt(match[1]);
    const startMin = match[2];
    const startPeriod = match[3];
    let endHour = parseInt(match[4]);
    const endMin = match[5];
    const endPeriod = match[6];
    
    if (startPeriod) {
      if (startPeriod.toUpperCase() === 'PM' && startHour !== 12) startHour += 12;
      if (startPeriod.toUpperCase() === 'AM' && startHour === 12) startHour = 0;
    }
    
    if (endPeriod) {
      if (endPeriod.toUpperCase() === 'PM' && endHour !== 12) endHour += 12;
      if (endPeriod.toUpperCase() === 'AM' && endHour === 12) endHour = 0;
    }
    
    return {
      start: `${startHour.toString().padStart(2, '0')}:${startMin}`,
      end: `${endHour.toString().padStart(2, '0')}:${endMin}`
    };
  };

  const handleUpdateTime = async () => {
    if (!nextVisit) return;
    
    if (!validateTimeDuration(startTime, endTime)) {
      return;
    }
    
    try {
      const timeRange = formatTimeRange(startTime, endTime);
      const dateStr = toLocalDateString(nextVisit.date); // Use toLocalDateString to avoid timezone issues
      
      const response = await axios.put(`/api/jobs/${nextVisit.job._id}`, {
        'schedule.preferredTime': timeRange,
        updateDate: dateStr
      });
      
      if (response.data.success) {
        setUpdating(true);
        await fetchMyJobs();
        setEditingTime(false);
        setTimeError('');
        setSuccessMessage('Visit time updated successfully!');
        setTimeout(() => setSuccessMessage(''), 3000);
        setUpdating(false);
      }
    } catch (error: any) {
      console.error('Error updating time:', error);
      alert(error.response?.data?.message || 'Failed to update time');
    }
  };

  const handleSaveTomorrowNote = async () => {
    if (!nextVisit) return;
    
    try {
      const dateStr = toLocalDateString(nextVisit.date);
      const response = await axios.post('/api/jobs/update-visit-schedule', {
        jobId: nextVisit.job._id,
        date: dateStr,
        notes: tomorrowNoteInput,
        caregiverId: nextVisit.job.assignedCaregiver._id
      });

      if (response.data.success) {
        setUpdating(true);
        await fetchMyJobs();
        setEditingTomorrowNote(false);
        setSuccessMessage('Note saved successfully!');
        setTimeout(() => setSuccessMessage(''), 3000);
        setUpdating(false);
      }
    } catch (error: any) {
      console.error('Error saving note:', error);
      alert(error.response?.data?.message || 'Failed to save note');
    }
  };

  // Helper to get Date object for start time
  const getStartTimeDate = (date: Date, timeString?: string) => {
      if (!timeString) return null;
      try {
          const startPart = timeString.split('-')[0].trim();
          const [time, period] = startPart.split(' ');
          let [hours, minutes] = time.split(':').map(Number);
          
          if (period === 'PM' && hours !== 12) hours += 12;
          if (period === 'AM' && hours === 12) hours = 0;
          
          const target = new Date(date);
          target.setHours(hours, minutes, 0, 0);
          return target;
      } catch (e) {
          return null;
      }
  };

  const nextVisit = getNextVisit();
  const [safetyStatus, setSafetyStatus] = useState<string | null>(null);

  const activeShiftData = (nextVisit && nextVisit.status === 'in-progress' && nextVisit.visit) ? {
    jobId: nextVisit.job._id,
    visitId: nextVisit.visit._id,
    correspondentName: `${nextVisit.job.assignedCaregiver.firstName} ${nextVisit.job.assignedCaregiver.lastName}`,
    correspondentPhoto: nextVisit.job.assignedCaregiver.profilePhoto,
    userType: 'customer' as const,
    hasActiveSafetyCheck: safetyStatus === 'safety_check_triggered' || safetyStatus === 'safety_check_active'
  } : undefined;

  /* Polling is now handled by ActiveShiftView via onSafetyStatusChange */
  
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-500"></div>
      </div>
    );
  }

  const activeJobs = myJobs.filter(job => job.status === 'in-progress' || job.status === 'accepted');
  const hasActiveJobs = activeJobs.length > 0;

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col font-sans animate-fade-in">
      
      {/* Success Message Banner */}
      {successMessage && (
        <div className="fixed top-20 left-1/2 transform -translate-x-1/2 z-50 animate-fade-in-down">
          <div className="bg-green-500 text-white px-6 py-3 rounded-lg shadow-lg flex items-center gap-2">
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
            </svg>
            <span className="font-medium">{successMessage}</span>
          </div>
        </div>
      )}

      <div className="flex-1 max-w-7xl mx-auto w-full px-4 sm:px-6 py-8 pb-24 sm:pb-8">

        {/* Welcome Section */}
        <div className="mb-8">
            <h1 className="text-3xl font-bold text-gray-900 tracking-tight mb-2">
              Hello, {user?.firstName} 👋
            </h1>
            <p className="text-gray-500">Here's your care schedule.</p>
        </div>

        {/* Active Shift Banner - High Priority */}
         {nextVisit && nextVisit.status === 'in-progress' && nextVisit.visit && (
             <div className="mb-8 rounded-2xl overflow-hidden shadow-xl border border-green-100 bg-white relative">
                 <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-green-400 to-emerald-500"></div>
                 <div className="p-6">
                    <div className="flex justify-between items-center mb-6">
                        <div className="flex items-center gap-3">
                            <span className="relative flex h-4 w-4">
                              <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-green-400 opacity-75"></span>
                              <span className="relative inline-flex rounded-full h-4 w-4 bg-green-500"></span>
                            </span>
                            <h2 className="text-xl font-bold text-gray-800">Visit in Progress</h2>
                        </div>
                        <div className="px-4 py-1.5 bg-green-50 text-green-700 text-sm font-bold rounded-full border border-green-100 uppercase tracking-wide">
                            Live Status
                        </div>
                    </div>
                    
                    <ActiveShiftView 
                       job={nextVisit.job} 
                       visit={nextVisit.visit} 
                       userType="customer" 
                       onShiftEnd={fetchMyJobs} 
                       compact={true}
                       onSafetyStatusChange={setSafetyStatus}
                     />
                 </div>
             </div>
         )}
        
        {/* Main Grid */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            
            {/* Left Col (Main) */}
            <div className="lg:col-span-2 space-y-8">
                
                {/* 1. Next Visit Card (Keep as is) */}
                {hasActiveJobs && nextVisit && nextVisit.status !== 'in-progress' && (
                    <div className="bg-white rounded-2xl p-6 shadow-sm border border-primary-100 relative overflow-hidden group hover:shadow-md transition-all">
                        <div className="absolute top-0 right-0 w-64 h-64 bg-primary-50 rounded-full -mr-32 -mt-32 opacity-50 group-hover:opacity-100 transition-opacity"></div>
                        
                        <div className="relative z-10 flex flex-col md:flex-row justify-between items-start md:items-center mb-6">
                            <div>
                                <span className="text-primary-600 font-bold tracking-wide uppercase text-xs mb-1 block">Up Next</span>
                                <h2 className="text-2xl font-bold text-gray-900 mb-1">
                                {nextVisit.date.toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric' }) === new Date().toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric' }) 
                                    ? 'Today\'s Visit' : 'Upcoming Visit'}
                                </h2>
                                <p className="text-gray-500 flex items-center gap-2">
                                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" /></svg>
                                {nextVisit.date.toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric' })}
                                </p>
                            </div>
                            
                            {/* Time Editor in header */}
                            <div className="mt-4 md:mt-0 flex flex-col items-center md:items-end gap-2">
                                {editingTime ? (
                                    <div className="bg-white p-3 rounded-xl border border-gray-200 shadow-sm z-20 relative">
                                        <div className="flex flex-col gap-2">
                                            <div className="flex gap-2">
                                                <input
                                                  type="time"
                                                  value={startTime}
                                                  onChange={(e) => setStartTime(e.target.value)}
                                                  className="px-2 py-1 text-sm border rounded bg-gray-50"
                                                />
                                                <span className="self-center">-</span>
                                                <input
                                                  type="time"
                                                  value={endTime}
                                                  onChange={(e) => setEndTime(e.target.value)}
                                                  className="px-2 py-1 text-sm border rounded bg-gray-50"
                                                />
                                            </div>
                                            <div className="flex gap-2 justify-end">
                                                <button onClick={handleUpdateTime} className="text-xs bg-primary-600 text-white px-3 py-1 rounded hover:bg-primary-700">Save</button>
                                                <button onClick={() => { setEditingTime(false); setTimeError(''); }} className="text-xs bg-gray-200 text-gray-700 px-3 py-1 rounded hover:bg-gray-300">Cancel</button>
                                            </div>
                                            {timeError && <p className="text-red-500 text-xs">{timeError}</p>}
                                        </div>
                                    </div>
                                ) : (
                                    <>
                                        <div className="flex items-center bg-primary-50 px-3 py-1.5 rounded-lg text-primary-800 font-bold border border-primary-100 text-sm cursor-pointer hover:bg-primary-100 transition-colors group" onClick={() => {
                                            setEditingTime(true);
                                            const currentTime = nextVisit.time || nextVisit.job.schedule.preferredTime;
                                            const parsed = parseTimeRange(currentTime);
                                            if (parsed) {
                                                setStartTime(parsed.start);
                                                setEndTime(parsed.end);
                                            } else {
                                                setStartTime('09:00');
                                                setEndTime('17:00');
                                            }
                                        }}>
                                            <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>
                                            {nextVisit.time || nextVisit.job.schedule.preferredTime}
                                            <span className="ml-2 bg-white/50 px-1.5 py-0.5 rounded text-[10px] uppercase text-primary-600 opacity-0 group-hover:opacity-100 transition-opacity">Edit</span>
                                        </div>

                                        {nextVisit.date.toLocaleDateString('en-US') === new Date().toLocaleDateString('en-US') && getStartTimeDate(nextVisit.date, nextVisit.time) && new Date() < getStartTimeDate(nextVisit.date, nextVisit.time)! && (
                                            <div className="flex flex-col items-center md:items-end animate-in fade-in slide-in-from-top-1 duration-500">
                                                <span className="text-[10px] font-bold text-gray-400 uppercase tracking-widest mb-1">Starts In</span>
                                                <CountdownTimer targetDate={getStartTimeDate(nextVisit.date, nextVisit.time)!} />
                                            </div>
                                        )}
                                    </>
                                )}
                            </div>
                        </div>

                        {/* Caregiver Info */}
                        <div className="flex items-center gap-4 mb-6 p-4 bg-gray-50 rounded-xl border border-gray-200/60">
                            <div className="flex-shrink-0">
                                {nextVisit.job.assignedCaregiver.profilePhoto ? (
                                <img
                                    src={getFileUrl(nextVisit.job.assignedCaregiver.profilePhoto)}
                                    alt="Caregiver"
                                    className="w-16 h-16 rounded-full object-cover border-2 border-white shadow-sm"
                                />
                                ) : (
                                <div className="w-16 h-16 rounded-full bg-primary-100 flex items-center justify-center border-2 border-white shadow-sm text-primary-600 font-bold text-xl">
                                    {nextVisit.job.assignedCaregiver.firstName[0]}
                                </div>
                                )}
                            </div>
                            <div>
                                <h3 className="text-lg font-bold text-gray-900">{nextVisit.job.assignedCaregiver.firstName} {nextVisit.job.assignedCaregiver.lastName}</h3>
                                <p className="text-sm text-gray-500">Your Caregiver • {nextVisit.job.serviceType.replace('-', ' ')}</p>
                                <div className="flex gap-4 mt-2">
                                     {nextVisit.job.assignedCaregiver.phone && (
                                        <a href={`tel:${nextVisit.job.assignedCaregiver.phone}`} className="text-xs text-primary-600 font-semibold flex items-center gap-1 hover:underline">
                                            <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" /></svg>
                                            Call
                                        </a>
                                     )}
                                     {nextVisit.job.assignedCaregiver.email && (
                                        <a href={`mailto:${nextVisit.job.assignedCaregiver.email}`} className="text-xs text-primary-600 font-semibold flex items-center gap-1 hover:underline">
                                            <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" /></svg>
                                            Email
                                        </a>
                                     )}
                                </div>
                            </div>
                        </div>

                        {/* Notes Editor */}
                        {editingTomorrowNote ? (
                             <div className="mb-4 bg-yellow-50 p-4 rounded-xl border border-yellow-100">
                                 <textarea
                                      value={tomorrowNoteInput}
                                      onChange={(e) => setTomorrowNoteInput(e.target.value)}
                                      placeholder="Add instructions for the caregiver..."
                                      className="w-full p-2 border border-yellow-200 rounded-lg text-sm bg-white mb-2"
                                      rows={3}
                                    />
                                    <div className="flex gap-2">
                                        <button onClick={handleSaveTomorrowNote} className="px-3 py-1 bg-yellow-600 text-white text-xs font-bold rounded hover:bg-yellow-700">Save Note</button>
                                        <button onClick={() => setEditingTomorrowNote(false)} className="px-3 py-1 bg-gray-200 text-gray-700 text-xs font-bold rounded hover:bg-gray-300">Cancel</button>
                                    </div>
                             </div>
                        ) : (
                            <div className="mb-4 bg-yellow-50 p-4 rounded-xl border border-yellow-100 cursor-pointer hover:bg-yellow-100/50 transition-colors" onClick={() => {
                                setEditingTomorrowNote(true);
                                setTomorrowNoteInput(nextVisit.notes || '');
                            }}>
                                <p className="text-yellow-800 text-sm font-medium flex items-start gap-2">
                                <svg className="w-5 h-5 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" /></svg>
                                {nextVisit.notes ? (
                                    <span className="italic">" {nextVisit.notes} "</span>
                                ) : (
                                    <span className="opacity-70 not-italic">Add a note for the caregiver...</span>
                                )}
                                <span className="ml-auto text-xs text-yellow-600 font-bold bg-yellow-100 px-2 py-0.5 rounded">EDIT</span>
                                </p>
                            </div>
                        )}
                       
                    </div>
                  )}

                  {/* Pending Reviews - always after next visit card, even on mobile */}
                  <PendingReviews />

                  {(!hasActiveJobs || (activeJobs.length === 0)) && (
                   <div className="bg-white rounded-2xl p-12 text-center border border-gray-100 shadow-sm">
                        <div className="w-20 h-20 bg-gray-50 rounded-full flex items-center justify-center mx-auto mb-4 text-gray-300">
                           <svg className="w-10 h-10" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" /></svg>
                        </div>
                        <h3 className="text-xl font-bold text-gray-900 mb-2">Find a Caregiver</h3>
                        <p className="text-gray-500 max-w-sm mx-auto mb-6">You don't have any active jobs. Start by finding a qualified caregiver for your needs.</p>
                        <button onClick={() => navigate('/customer/find-caregivers')} className="px-8 py-3 bg-primary-600 text-white rounded-xl hover:bg-primary-700 font-bold shadow-lg shadow-primary-200 transition-all">
                            Start Search
                        </button>
                   </div>
                )}

                {/* 2. Active Jobs & Calendars (Moved from Right Sidebar to Main Column) */}
                {hasActiveJobs && activeJobs.map((job) => (
                    <div key={job._id} className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden">
                        <div className="p-6 border-b border-gray-100 bg-gray-50 flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
                            <div>
                                <h3 className="text-lg font-bold text-gray-900">{job.title}</h3>
                                <p className="text-sm text-gray-500">Caregiver: <span className="font-medium text-gray-700">{job.assignedCaregiver.firstName} {job.assignedCaregiver.lastName}</span></p>
                            </div>
                            <button 
                                onClick={() => navigate(`/jobs/${job._id}`)} 
                                className="px-4 py-2 bg-white border border-gray-200 text-gray-700 text-sm font-bold rounded-lg hover:bg-gray-50 transition-colors shadow-sm flex items-center gap-2"
                            >
                                <span>Plan Details</span>
                                <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" /></svg>
                            </button>
                        </div>
                        <div className="p-0">
                            <CustomerVisitCalendar
                                jobId={job._id}
                                startDate={parseLocalDate(job.schedule.startDate)}
                                endDate={parseLocalDate(job.schedule.endDate)}
                                visitSchedule={job.visitSchedule?.map((vs: any) => ({
                                    date: parseLocalDate(vs.date),
                                    isVisitDay: vs.isVisitDay,
                                    notes: vs.notes,
                                    time: vs.time
                                })) || []}
                                specialInstructions={job.description}
                                caregiverId={job.assignedCaregiver._id}
                                onScheduleUpdate={fetchMyJobs}
                                currentPreferredTime={job.schedule.preferredTime}
                            />
                        </div>
                    </div>

                ))}

                {/* Pending Reviews - now shown after first card */}
                <PendingReviews />
            </div>

            {/* Right Sidebar (Secondary) */}
            <div className="space-y-8">
              {/* Pro Tip or additional info */}
              <div className="bg-gradient-to-br from-primary-500 to-primary-700 rounded-2xl p-6 text-white shadow-lg relative overflow-hidden">
                <div className="relative z-10">
                  <h3 className="font-bold text-lg mb-2">Need Help?</h3>
                  <p className="text-primary-100 text-sm mb-4">Our support team is available 24/7 to assist you with any questions.</p>
                  <button className="bg-white/10 hover:bg-white/20 text-white text-xs font-bold py-2 px-4 rounded-lg transition-colors border border-white/20">
                    Contact Support
                  </button>
                </div>
                <div className="absolute -bottom-10 -right-10 w-32 h-32 bg-white opacity-10 rounded-full"></div>
              </div>
            </div>
        </div>

      </div>

      <ShohAI activeShift={activeShiftData} />
    </div>
  );
};

export default CustomerDashboard;
